import React, { useCallback, useEffect, useRef, useState } from "react";
import { API_URL, setViewAs } from "../auth";
import { readCache, writeCache } from "../dataCache";
import MobileScrollArea from "./MobileScrollArea";

// Admin control center. Only rendered for users whose role is "admin".
// Lets the admin see workspace-wide totals, browse every user, drill into a
// single user's data, change roles, and delete a user together with all of
// their data. Every request hits the /api/admin/* routes which are protected
// server-side by requireAdmin, so this UI is a convenience layer only.
export default function AdminPanel() {
  const [overview, setOverview] = useState(() => readCache("admin_overview", null));
  const [users, setUsers] = useState(() => readCache("admin_users", []));
  const [loading, setLoading] = useState(() => readCache("admin_users", null) === null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [toast, setToast] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const flash = useCallback((msg) => {
    setToast(msg);
    window.clearTimeout(window.__adminToastTimer);
    window.__adminToastTimer = window.setTimeout(() => setToast(""), 2600);
  }, []);

  const loadAll = useCallback(async () => {
    if (readCache("admin_users", null) === null) setLoading(true);
    setError("");
    try {
      const [oRes, uRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/overview`),
        fetch(`${API_URL}/api/admin/users`)
      ]);
      if (oRes.status === 403 || uRes.status === 403) {
        throw new Error("You do not have permission to view the admin panel.");
      }
      const oData = await oRes.json().catch(() => ({}));
      const uData = await uRes.json().catch(() => ({}));
      if (!oRes.ok) throw new Error(oData.message || "Failed to load overview.");
      if (!uRes.ok) throw new Error(uData.message || "Failed to load users.");
      const nextUsers = Array.isArray(uData.users) ? uData.users : [];
      setOverview(oData);
      setUsers(nextUsers);
      writeCache("admin_overview", oData);
      writeCache("admin_users", nextUsers);
    } catch (err) {
      setError(err.message || "Failed to load admin data.");
    } finally {
      setLoading(false);
    }
  }, []);

  // REQ-32: manual Refresh always shows visible feedback (loading dots) and
  // stays visible for a minimum of 3 seconds so the refresh is clearly felt,
  // even when cached data returns instantly.
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    const startedAt = Date.now();
    try {
      await loadAll();
    } finally {
      const elapsed = Date.now() - startedAt;
      if (elapsed < 3000) await new Promise((r) => setTimeout(r, 3000 - elapsed));
      setRefreshing(false);
    }
  }, [loadAll]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // REQ-25: Live auto-refresh for the admin panel, exactly like the user panel's
  // Performance page. Every 4s we silently re-fetch workspace totals + the user
  // list so Opens/Clicks/Emails Sent update in near real-time. If a user-detail
  // drawer is open, it refreshes too -- without any loading flicker. Bots are
  // already excluded server-side, so these numbers stay fake-free.
  const detailIdRef = useRef(null);
  useEffect(() => {
    detailIdRef.current = detail?.user?.id || null;
  }, [detail]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      loadAll();
      const openId = detailIdRef.current;
      if (openId) {
        fetch(`${API_URL}/api/admin/users/${openId}`)
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => {
            if (d && d.user) setDetail(d);
          })
          .catch(() => {});
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [loadAll]);

  const openDetail = useCallback(async (id) => {
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${id}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed to load user.");
      setDetail(data);
    } catch (err) {
      flash(err.message || "Failed to load user.");
    } finally {
      setDetailLoading(false);
    }
  }, [flash]);

  const changeRole = useCallback(async (user, nextRole) => {
    setBusyId(user.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${user.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role: nextRole })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed to update role.");
      flash(`Role updated to ${nextRole}.`);
      await loadAll();
    } catch (err) {
      flash(err.message || "Failed to update role.");
    } finally {
      setBusyId("");
    }
  }, [flash, loadAll]);

  const deleteUser = useCallback(async (user) => {
    const ok = window.confirm(
      `Delete ${user.email || "this user"} and ALL of their data?\n\nThis removes their contacts, lists, tags, segments, senders, SMTPs, campaigns, queue jobs, events and polls. This cannot be undone.`
    );
    if (!ok) return;
    setBusyId(user.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${user.id}`, { method: "DELETE" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed to delete user.");
      flash("User and all their data deleted.");
      if (detail && detail.user && detail.user.id === user.id) setDetail(null);
      await loadAll();
    } catch (err) {
      flash(err.message || "Failed to delete user.");
    } finally {
      setBusyId("");
    }
  }, [flash, loadAll, detail]);

  const term = search.trim().toLowerCase();
  const visibleUsers = term
    ? users.filter((u) =>
        String(u.name || "").toLowerCase().includes(term) ||
        String(u.email || "").toLowerCase().includes(term))
    : users;

  const cards = [
    { label: "Total Users", value: overview?.totalUsers },
    { label: "Total Contacts", value: overview?.totalContacts },
    { label: "Total Campaigns", value: overview?.totalCampaigns },
    { label: "Emails Sent", value: overview?.totalEmailsSent }
  ];

  return (
    <div className="sx-admin">
      {toast ? <div className="sx-admin-toast">{toast}</div> : null}

      <div className="sx-admin-head">
        <div>
          <h2 className="sx-admin-title">Admin Panel</h2>
          <p className="sx-admin-sub">Manage every user and their data across the workspace.</p>
        </div>
        <button className="sx-admin-refresh" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? (
            <>Refreshing<span className="sx-refresh-dots"><i></i><i></i><i></i></span></>
          ) : "Refresh"}
        </button>
      </div>

      {error ? <div className="sx-admin-error">{error}</div> : null}

      <div className="sx-admin-cards">
        {cards.map((c) => (
          <div className="sx-admin-card" key={c.label}>
            <div className="sx-admin-card-value">{overview ? (c.value ?? 0) : "…"}</div>
            <div className="sx-admin-card-label">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="sx-admin-toolbar">
        <input
          className="sx-admin-search"
          placeholder="Search users by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="sx-admin-count">{visibleUsers.length} user{visibleUsers.length === 1 ? "" : "s"}</span>
      </div>

      <MobileScrollArea className="sx-admin-table-wrap" ariaLabel="users table">
        <table className="sx-admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Contacts</th>
              <th>Campaigns</th>
              <th>Emails Sent</th>
              <th>Opens</th>
              <th>Clicks</th>
              <th className="sx-admin-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && visibleUsers.length === 0 ? (
              <tr><td colSpan={8} className="sx-admin-empty">No users found.</td></tr>
            ) : null}
            {visibleUsers.map((u) => {
              const c = u.counts || {};
              const isBusy = busyId === u.id;
              return (
                <tr key={u.id}>
                  <td>
                    <div className="sx-admin-user">
                      <span className="sx-admin-avatar">{String(u.name || u.email || "U").charAt(0).toUpperCase()}</span>
                      <div className="sx-admin-user-meta">
                        <div className="sx-admin-user-name">{u.name || "(no name)"}</div>
                        <div className="sx-admin-user-email">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={u.role === "admin" ? "sx-badge sx-badge-admin" : "sx-badge"}>{u.role}</span>
                    {u.isPrimaryAdmin ? <span className="sx-admin-primary">primary</span> : null}
                  </td>
                  <td>{c.contacts ?? 0}</td>
                  <td>{c.campaigns ?? 0}</td>
                  <td>{c.emailsSent ?? 0}</td>
                  <td>{c.opens ?? 0}</td>
                  <td>{c.clicks ?? 0}</td>
                  <td className="sx-admin-actions">
                    <button className="sx-admin-btn" disabled={isBusy} onClick={() => openDetail(u.id)}>View</button>
                    <button
                      className="sx-admin-btn sx-admin-btn-primary"
                      disabled={isBusy}
                      title="Open the app showing only this user's data"
                      onClick={() => {
                        setViewAs({ id: u.id, name: u.name || "", email: u.email || "" });
                        window.location.hash = "#/Dashboard";
                        window.location.reload();
                      }}
                    >View data</button>
                    {u.isPrimaryAdmin ? null : (
                      <>
                        {u.role === "admin" ? (
                          <button className="sx-admin-btn" disabled={isBusy} onClick={() => changeRole(u, "user")}>Make user</button>
                        ) : (
                          <button className="sx-admin-btn" disabled={isBusy} onClick={() => changeRole(u, "admin")}>Make admin</button>
                        )}
                        <button className="sx-admin-btn sx-admin-btn-danger" disabled={isBusy} onClick={() => deleteUser(u)}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </MobileScrollArea>

      {detailLoading ? <div className="sx-admin-drawer-loading">Loading user…</div> : null}

      {detail && detail.user ? (
        <div className="sx-admin-drawer-overlay" onClick={() => setDetail(null)}>
          <div className="sx-admin-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="sx-admin-drawer-head">
              <div>
                <h3>{detail.user.name || "(no name)"}</h3>
                <p>{detail.user.email} · <span className={detail.user.role === "admin" ? "sx-badge sx-badge-admin" : "sx-badge"}>{detail.user.role}</span></p>
              </div>
              <button className="sx-admin-btn" onClick={() => setDetail(null)}>Close</button>
            </div>

            <div className="sx-admin-detail-counts">
              {[
                ["Contacts", detail.counts?.contacts],
                ["Lists", detail.counts?.lists],
                ["Tags", detail.counts?.tags],
                ["Segments", detail.counts?.segments],
                ["Senders", detail.counts?.senders],
                ["SMTPs", detail.counts?.smtp],
                ["Campaigns", detail.counts?.campaigns],
                ["Emails Sent", detail.counts?.emailsSent],
                ["Opens", detail.counts?.opens],
                ["Clicks", detail.counts?.clicks]
              ].map(([label, value]) => (
                <div className="sx-admin-mini" key={label}>
                  <div className="sx-admin-mini-value">{value ?? 0}</div>
                  <div className="sx-admin-mini-label">{label}</div>
                </div>
              ))}
            </div>

            <div className="sx-admin-detail-cols">
              <div className="sx-admin-detail-col">
                <h4>Recent Contacts</h4>
                {(detail.contacts || []).length === 0 ? <p className="sx-admin-muted">No contacts.</p> : (
                  <ul className="sx-admin-list">
                    {detail.contacts.map((ct) => (
                      <li key={ct._id}>
                        <span>{ct.name || ct.email || "(no name)"}</span>
                        <span className="sx-admin-muted">{ct.email}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="sx-admin-detail-col">
                <h4>Recent Campaigns</h4>
                {(detail.campaigns || []).length === 0 ? <p className="sx-admin-muted">No campaigns.</p> : (
                  <ul className="sx-admin-list">
                    {detail.campaigns.map((cp) => (
                      <li key={cp._id}>
                        <span>{cp.name || "(untitled)"}</span>
                        <span className="sx-admin-muted">{cp.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
